var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { asPosixPath, CollisionFacet, CollisionMeshShapeType, FacetFactory, MeshFacet, PATH_SEPERATOR, PropertyParser, PropertyType, ScriptComposeFacet, ScriptPropertyType } from "flavr-common";
import { Quaternion, Vector3 } from "flavr-math";
import { modelPool } from "@modules/model";
import { ModelPropertyParser } from "@modules/blueprint/parser/ModelPropertyParser";
const _RESERVED_CHARS_RE = "\\[\\]\\.:\\/";
const _reservedRe = new RegExp("[" + _RESERVED_CHARS_RE + "]", "g");
const BLUEPRINT_PARSE_ABORT_ERROR = "Blueprint parsing aborted because the target parent model was destroyed";
export class BlueprintParserContext {
    constructor(meshUrl, rootModel) {
        this.meshUrl = meshUrl;
        this.rootModel = rootModel;
        this.modelNameToModel = new Map();
        this.modelNameToMeshPath = new Map();
        this.primitiveMap = new Map();
        this.assets = new Map();
        this.modelResults = [];
    }
    parseTransform(obj, position, orientation, scale) {
        position.copy(obj.position);
        orientation.copy(obj.quaternion);
        scale.copy(obj.scale);
    }
    createSubPath(parentPath, name) {
        return parentPath + PATH_SEPERATOR + name;
    }
    getFileIdByRelativePath(relativePath) {
        let unixPath = asPosixPath(relativePath);
        if (unixPath.startsWith("//")) {
            unixPath = unixPath.substring(2);
        }
        else if (unixPath.startsWith("/")) {
            unixPath = unixPath.substring(1);
        }
        unixPath = unixPath.replace(/(\\\\)|\\/g, "/");
        const asset = this.assets.get(unixPath);
        if (asset === undefined)
            return relativePath;
        return asset.fileId;
    }
}
export class GltfFacetsExtensions {
    constructor() {
        this.name = "FLAVR_facets";
        /// Property resolution ///
        this.createResolveScriptPropertyCallback = (context, facetType) => {
            return (propDesc, value, meta) => {
                if (facetType === ScriptComposeFacet.typestatic) {
                    const prop = this.resolveScriptCompositeProperty(context, value, meta);
                    if (prop !== undefined)
                        return prop;
                }
                switch (propDesc.type) {
                    case PropertyType.AssetPath:
                    case PropertyType.AssetPathSafe:
                        return context.getFileIdByRelativePath(value);
                }
                return value;
            };
        };
    }
    setParser(parser) { this.parser = parser; }
    // This method is called via callback mechanism! It is not unused.
    afterRoot(gltf) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.parser === undefined)
                return;
            if (this.context === undefined)
                return;
            // ToDo: parse assets info from from scene
            // This code structure need to be like this because of asynchronous behavior
            // Do not merge loops for now!
            for (const scene of gltf.scenes) {
                for (const obj of scene.children) {
                    this.context.modelResults.push(this.gatherModel(this.parser, this.context, obj, this.context.rootModel, ""));
                }
            }
            for (const scene of gltf.scenes) {
                for (const obj of scene.children) {
                    this.parseModelFromObject3D(this.parser, this.context, obj, "");
                }
            }
        });
    }
    static isParseAbortError(error) {
        return error instanceof Error && error.message === BLUEPRINT_PARSE_ABORT_ERROR;
    }
    gatherModel(parser, context, obj, parentModel, parentPath) {
        const name = obj.name ? this.sanitizeNodeName(obj.name) : obj.uuid;
        const modelPath = context.createSubPath(parentPath, name);
        let model;
        try {
            this.ensureParentModelExists(parentModel);
            model = modelPool.createModel(name, parentModel);
        }
        catch (error) {
            if (GltfFacetsExtensions.isParseAbortError(error)) {
                throw error;
            }
            console.warn("[blueprint] Aborting model gathering while parsing GLTF", error);
            throw new Error(BLUEPRINT_PARSE_ABORT_ERROR);
        }
        const position = new Vector3();
        const orientation = new Quaternion();
        const scale = new Vector3(1, 1, 1);
        context.parseTransform(obj, position, orientation, scale);
        model.setTransformComponents(position, orientation, scale, { updateFacet: true });
        if (obj.isMesh) {
            context.modelNameToMeshPath.set(name, modelPath);
        }
        this.gatherMesh(parser, context, obj, modelPath, model);
        context.modelNameToModel.set(name, model);
        for (const child of obj.children) {
            this.gatherModel(parser, context, child, model, modelPath);
        }
        return model;
    }
    gatherMesh(parser, context, obj, parentPath, parent) {
        const nodeDef = this.getNodeDef(parser, obj);
        if (!nodeDef)
            return;
        const meshIndex = nodeDef.mesh;
        if (meshIndex === undefined)
            return;
        const meshDef = parser.json.meshes[meshIndex];
        const primitives = meshDef.primitives;
        // use node name
        if (primitives.length <= 1)
            return;
        const meshInfos = [];
        for (let i = 0; i < primitives.length; i++) {
            const name = meshDef.name ? `${meshDef.name}_${i}$` : `mesh_${meshIndex}`;
            const path = context.createSubPath(parentPath, name);
            let model;
            try {
                this.ensureParentModelExists(parent);
                model = modelPool.createModel(name, parent);
            }
            catch (error) {
                if (GltfFacetsExtensions.isParseAbortError(error)) {
                    throw error;
                }
                console.warn("[blueprint] Aborting mesh gathering while parsing GLTF", error);
                throw new Error(BLUEPRINT_PARSE_ABORT_ERROR);
            }
            meshInfos.push({
                name,
                path,
                model
            });
        }
        context.primitiveMap.set(parentPath, meshInfos);
    }
    parseModelFromObject3D(parser, context, obj, parentPath) {
        const name = obj.name ? this.sanitizeNodeName(obj.name) : obj.uuid;
        const model = context.modelNameToModel.get(name);
        if (!model)
            return;
        const modelPath = context.createSubPath(parentPath, name);
        this.parseMesh(parser, context, model, obj, modelPath);
        this.parseFacetsFromUserData(parser, context, obj, model, modelPath);
        for (const child of obj.children) {
            const modelPath = context.createSubPath(parentPath, name);
            this.parseModelFromObject3D(parser, context, child, modelPath);
        }
    }
    parseMesh(parser, context, model, obj, modelPath) {
        const nodeDef = this.getNodeDef(parser, obj);
        if (!nodeDef)
            return;
        if (!nodeDef.extensions)
            return;
        const facets = nodeDef.extensions[this.name];
        if (!facets)
            return;
        const facet = facets[MeshFacet.typestatic];
        if (!facet)
            return;
        if (!PropertyParser.booleanSafe(facet, "renderable", true))
            return;
        const meshIndex = nodeDef.mesh;
        if (meshIndex === undefined)
            return;
        const meshDef = parser.json.meshes[meshIndex];
        const primitives = meshDef.primitives;
        const castShadow = PropertyParser.booleanSafe(facet, "castShadow", true);
        const receiveShadow = PropertyParser.booleanSafe(facet, "receiveShadow", true);
        const reflectionProbeUid = ModelPropertyParser.modelSafe(facet.params, "reflectionProbeUid", context);
        const ignoreCubeMapRendering = PropertyParser.booleanSafe(facet.params, "ignoreCubeMapRendering", false);
        const frustumCulled = PropertyParser.booleanSafe(facet.params, "frustumCulled", false);
        const isStatic = PropertyParser.booleanSafe(facet.params, "isStatic", undefined);
        if (primitives.length > 1) {
            const meshInfos = context.primitiveMap.get(modelPath);
            if (meshInfos) {
                for (const info of meshInfos) {
                    const pieceName = info.path;
                    info.model.setFacet(new MeshFacet(context.meshUrl, undefined, castShadow, receiveShadow, {
                        needsRescale: false,
                        pieceName,
                        reflectionProbeUid,
                        ignoreCubeMapRendering,
                        frustumCulled,
                        isStatic,
                        showBusyIndicator: false
                    }));
                }
            }
        }
        else {
            model.setFacet(new MeshFacet(context.meshUrl, undefined, castShadow, receiveShadow, {
                needsRescale: false,
                pieceName: modelPath,
                reflectionProbeUid,
                ignoreCubeMapRendering,
                frustumCulled,
                isStatic,
                showBusyIndicator: false
            }));
        }
    }
    parseFacetsFromUserData(parser, context, obj, model, modelPath) {
        const nodeDef = this.getNodeDef(parser, obj);
        if ((nodeDef === null || nodeDef === void 0 ? void 0 : nodeDef.extensions) === undefined)
            return;
        const facets = nodeDef.extensions[this.name];
        if (facets === undefined)
            return;
        const onResolveProperty = (propDesc, value, meta) => {
            switch (propDesc.type) {
                case PropertyType.AssetPath:
                case PropertyType.AssetPathSafe:
                    return context.getFileIdByRelativePath(value);
            }
            // hack for mesh shapes
            if (meta.facetType === CollisionFacet.typestatic) {
                if (propDesc.type === PropertyType.EnumUint8 && meta.propName === "shape" && (value === "MESH" || value === "HULL" || value === "CONVEX_HULL")) {
                    return {
                        shape: this.toMeshShapeType(value),
                        meshUrl: context.meshUrl,
                        pieceName: modelPath
                    };
                }
                // Compound shapes aren't baked here - the client derives the element list live from whichever
                // descendants have their own CollisionFacet, so this only needs to mark the shape as compound.
                if (propDesc.type === PropertyType.EnumUint8 && meta.propName === "shape" && (value === "COMPOUND" || value === 12)) {
                    return [];
                }
            }
            return value;
        };
        for (const [facetType, data] of Object.entries(facets)) {
            if (facetType === MeshFacet.typestatic)
                continue;
            if (!data)
                continue;
            if (this.isObjectEmpty(data))
                continue;
            if (facetType === ScriptComposeFacet.typestatic) {
                if (this.loadAssets === undefined || this.loadAssets)
                    try {
                        const onResolveScriptProperty = this.createResolveScriptPropertyCallback(context, facetType);
                        const facet = FacetFactory.createFromObjectByTypeString(data, facetType, true, onResolveScriptProperty);
                        if (facet === undefined) {
                            console.warn("[blueprint] GltfFacetsExtension: Facet could not be created!");
                            continue;
                        }
                        model.setFacet(facet);
                    }
                    catch (error) {
                        console.error(`Can't create facet ${facetType}`, error);
                    }
                continue;
            }
            if (this.loadAssets === undefined || this.loadAssets)
                try {
                    const facet = FacetFactory.createFromObjectByTypeString(data, facetType, true, onResolveProperty);
                    if (!facet) {
                        console.warn("GltfFacetsExtension: Facet could not be created!");
                        continue;
                    }
                    model.setFacet(facet);
                }
                catch (error) {
                    console.error(`Can't create facet ${facetType}`, error);
                }
        }
    }
    resolveScriptCompositeProperty(context, value, meta) {
        var _a, _b, _c, _d;
        if (!(meta.propName === "value" && meta.parentData && meta.parentData.propDesc.type === PropertyType.Struct))
            return undefined;
        const propTypeDesc = meta.parentData.propDesc.properties.type;
        if (propTypeDesc) {
            const typeValue = meta.parentData.obj.type;
            switch (typeValue) {
                case ScriptPropertyType.Property: {
                    const valueType = typeof value;
                    try {
                        value = JSON.parse(valueType === "string" ? value.toLowerCase() : value);
                    }
                    catch (_e) {
                        value = value.toString();
                    }
                    return value;
                }
                case ScriptPropertyType.Model: {
                    let uid;
                    const typeValue = typeof value;
                    if (typeValue === "object") {
                        if (value.type === "Object" && value.name) {
                            const name = this.sanitizeNodeName(value.name);
                            uid = (_b = (_a = context.modelNameToModel.get(name)) === null || _a === void 0 ? void 0 : _a.getUID()) !== null && _b !== void 0 ? _b : uid;
                        }
                    }
                    else if (typeValue === "string") {
                        uid = (_d = (_c = context.modelNameToModel.get(value)) === null || _c === void 0 ? void 0 : _c.getUID()) !== null && _d !== void 0 ? _d : uid;
                    }
                    else if (typeValue === "number") {
                        // ToDo check if model in room
                        uid = modelPool.hasModel(value) ? value : uid;
                    }
                    return uid;
                }
                case ScriptPropertyType.Script:
                    return null;
                default:
                    console.warn(`Unsupported script property type ${PropertyType[propTypeDesc.type]} with name ${propTypeDesc.description}`, value, meta);
            }
        }
        return null;
    }
    /// Helper functions ///
    sanitizeNodeName(name) {
        return name.replace(/\s/g, "_").replace(_reservedRe, "");
    }
    isObjectEmpty(obj) {
        if (typeof obj !== "object")
            return true;
        // This is the fastest solution to check empty object
        // eslint-disable-next-line no-unreachable-loop
        for (const _o in obj)
            return false;
        return true;
    }
    toMeshShapeType(shapeType) {
        switch (shapeType) {
            case "MESH":
                return CollisionMeshShapeType.MESH;
            case "HULL":
            case "CONVEX_HULL":
                return CollisionMeshShapeType.HULL;
        }
        return CollisionMeshShapeType.MESH;
    }
    getNodeDef(parser, obj) {
        const reference = parser.associations.get(obj);
        if (reference === undefined)
            return undefined;
        if (reference.nodes === undefined)
            return undefined;
        const index = reference.nodes;
        return parser.json.nodes[index];
    }
    ensureParentModelExists(parentModel) {
        if (!modelPool.hasModel(parentModel.getUID())) {
            throw new Error(BLUEPRINT_PARSE_ABORT_ERROR);
        }
    }
}
